import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import albumSocialIcon from '../../icons/social_icons/album_social_icon.png'
import discordSocialIcon from '../../icons/social_icons/discord_social_icon.png'
import instaSocialIcon from '../../icons/social_icons/insta_social_icon.png'
import merchSocialIcon from '../../icons/social_icons/merch_social_icon.png'
import ticketsSocialIcon from '../../icons/social_icons/tickets_social_icon.png'
import ticketmasterSocialIcon from '../../icons/social_icons/ticketmaster_active.png'
import tiktokSocialIcon from '../../icons/social_icons/tiktok_social_icon.png'
import xSocialIcon from '../../icons/social_icons/x-logo.png'
import youtubeSocialIcon from '../../icons/social_icons/youtube_social_icon.png'
import classes from './index.module.css'
import { permissions } from '../../../constants/roles'
import useReadOnly from '../../../services/hooks/useReadOnly'
import useAdminAccess from '../../../services/hooks/useAdminAccess'

export default function ArtistProfileHeader({ artist, onEditClick }) {
  const { isReadOnly: isArtistReadOnly } = useReadOnly(
    permissions.r.music_artist
  )
  const { isInternal } = useAdminAccess()

  const [socialMediaUrls, setSocialMediaUrls] = useState({
    youtube: '',
    instagram: '',
    twitter: '',
    tiktok: '',
    discord: '',
  })
  const [additionalUrls, setAdditionalUrls] = useState({
    merchandise: '',
    tour: '',
    impact: '',
    ticketmaster: '',
  })

  useEffect(() => {
    if (artist) {
      const socialMedia = artist.social_media || []
      const links = artist.links || []

      const socialMediaMap = {}
      socialMedia.forEach(item => {
        socialMediaMap[item.f_id] = item.url
      })

      const additionalUrlsMap = {}
      links.forEach(item => {
        additionalUrlsMap[item.f_id] = item.url
      })
      setSocialMediaUrls(socialMediaMap)
      setAdditionalUrls(additionalUrlsMap)
    }
  }, [artist])

  const renderSocialMediaIcon = (url, icon, isTicketmaster = false) => {
    // For Ticketmaster, handle both full URLs and just IDs
    const impactAffiliateId = process.env.REACT_APP_IMPACT_AFFILIATE_ID;
    let finalUrl = url;
    
    if (isTicketmaster && url) {
      // Check if it's already a full URL or just an ID
      if (url.includes('ticketmaster.com')) {
        // It's already a full URL, just use it
        finalUrl = url;
      } else {
        // It's just an ID, construct the URL
        finalUrl = `https://www.ticketmaster.com/artist/${url}`;
      }
      
      // Add Impact affiliate tracking if configured
      if (impactAffiliateId && !finalUrl.includes('irgwc=')) {
        // Add tracking parameters if not already present
        finalUrl += finalUrl.includes('?') ? '&' : '?';
        finalUrl += `irgwc=1&camref=${impactAffiliateId}`;
      }
    }
    
    return finalUrl ? (
      <a href={finalUrl} target="_blank" rel="noopener noreferrer">
        <img
          className={classes.socialIconActive}
          src={icon}
          alt="Active Social Icon"
        />
      </a>
    ) : (
      <img
        className={classes.socialIconInactive}
        src={icon}
        alt="Inactive Social Icon"
      />
    )
  }

  return (
    <div className={classes.wrapper}>
      {artist.image.url ? (
        <img
          className={classes.image}
          src={artist.image.url}
          alt="Artist Profile"
        />
      ) : (
        <div className={classes.emptyImage}>
          <p className={classes.emptyImageText}>
            {artist.name.charAt(0).toUpperCase()}
          </p>
        </div>
      )}
      <div className={classes.dataWrapper}>
        <div className={classes.nameWrapper}>
          <p className={classes.name}>{artist.name}</p>
          {isInternal && artist.is_restricted && (
            <span
              className={classes.restrictedTag}
              title="This artist has been restricted by their label or publisher. Non-internal users will see an empty profile with a warning banner."
            >Restricted Artist</span>
          )}
          {!isArtistReadOnly && (
            <img
              width="24"
              height="24"
              src={require('../../../Components/icons/edit-icon.png')}
              alt={'Edit icon'}
              onClick={onEditClick}
              className={classes.editIcon}
            />
          )}
        </div>
        <div className={classes.socialElementsWrapper}>
          <div className={classes.socialElementsFirstRow}>
            {renderSocialMediaIcon(socialMediaUrls.youtube, youtubeSocialIcon)}
            {renderSocialMediaIcon(socialMediaUrls.instagram, instaSocialIcon)}
            {renderSocialMediaIcon(socialMediaUrls.twitter, xSocialIcon)}
            {renderSocialMediaIcon(socialMediaUrls.tiktok, tiktokSocialIcon)}
          </div>
          <div className={classes.socialElementsSecondRow}>
            {renderSocialMediaIcon(socialMediaUrls.discord, discordSocialIcon)}
            {renderSocialMediaIcon(additionalUrls.merchandise, merchSocialIcon)}
            {isInternal && renderSocialMediaIcon(additionalUrls.tour, ticketsSocialIcon)}
            {isInternal && renderSocialMediaIcon(additionalUrls.ticketmaster, ticketmasterSocialIcon, true)}
            {renderSocialMediaIcon(additionalUrls.impact, albumSocialIcon)}
          </div>
        </div>
      </div>
    </div>
  )
}

ArtistProfileHeader.propTypes = {
  artist: PropTypes.object.isRequired,
  onEditClick: PropTypes.func.isRequired,
}
